import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { pill, scrollBody, actionBtn, ProgressHeader, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow } from '../../auth/patientAuthFlow';

const genders = ['Male', 'Female', 'Other'];
const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const languages = ['English', 'Hindi', 'Tamil', 'Telugu'];

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  color: T.textTer,
  fontFamily: T.font,
  marginBottom: 4,
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: `1px solid ${T.border}`,
  background: T.ice,
  fontSize: 12,
  fontFamily: T.font,
  color: T.text,
  outline: 'none',
  boxSizing: 'border-box',
};

export default function PatRegBasicScreen() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('Male');
  const [bloodGroup, setBloodGroup] = useState('A+');
  const [language, setLanguage] = useState('English');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleNext = async () => {
    setError('');

    if (!fullName.trim()) {
      setError('Enter your full name.');
      return;
    }
    if (!phone.trim() && !email.trim()) {
      setError('Enter your phone number or email.');
      return;
    }
    if (phone.trim() && phone.trim().length !== 10) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }

    // Catch a duplicate account up front instead of only at the very end of
    // the 8-step wizard, when the backend's signup call would reject it.
    setChecking(true);
    const avail = await patientAuthFlow.backendCheckAvailability({
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
    });
    setChecking(false);

    if (avail.success) {
      if (avail.phoneExists) {
        setError('An account with this phone number already exists. Please log in instead.');
        return;
      }
      if (avail.emailExists) {
        setError('An account with this email already exists. Please log in instead.');
        return;
      }
    }
    // If the check itself failed (e.g. backend unreachable), don't block the
    // wizard on it — final signup still enforces uniqueness as a safety net.

    patientAuthFlow.completeBasic({ fullName, dob, gender, bloodGroup });
    patientAuthFlow.completeContact({ phone, email });
    navigate('/patient/register/contact');
  };

  return (
    <WebEntryLayout>
    <div style={{ flex: 1, background: T.white, display: 'flex', flexDirection: 'column', fontFamily: T.font }}>
      <ProgressHeader title="Basic Details" step={1} total={8} />

      <div style={scrollBody}>
        {/* Full Name */}
        <div>
          <div style={labelStyle}>Full Name</div>
          <input
            type="text"
            placeholder="Enter your full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {/* Phone */}
        <div>
          <div style={labelStyle}>Phone</div>
          <div style={{ display: 'flex', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: T.ice, borderRight: `1px solid ${T.border}`, fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.font, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              +91
            </div>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              style={{ ...fieldStyle, border: 'none', borderRadius: 0 }}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <div style={labelStyle}>Email</div>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {/* Date of Birth */}
        <div>
          <div style={labelStyle}>Date of Birth</div>
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            style={fieldStyle}
          />
        </div>

        {/* Gender */}
        <div>
          <div style={labelStyle}>Gender</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {genders.map((g) => (
              <div
                key={g}
                onClick={() => setGender(g)}
                style={{ ...pill(g, T.teal, gender === g), cursor: 'pointer' }}
              >
                {g}
              </div>
            ))}
          </div>
        </div>

        {/* Blood Group */}
        <div>
          <div style={labelStyle}>Blood Group</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {bloodGroups.map((bg) => (
              <div
                key={bg}
                onClick={() => setBloodGroup(bg)}
                style={{ ...pill(bg, T.navy, bloodGroup === bg), cursor: 'pointer' }}
              >
                {bg}
              </div>
            ))}
          </div>
        </div>

        {/* Preferred Language */}
        <div>
          <div style={labelStyle}>Preferred Language</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {languages.map((l) => (
              <div
                key={l}
                onClick={() => setLanguage(l)}
                style={{ ...pill(l, T.purple, language === l), cursor: 'pointer' }}
              >
                {l}
              </div>
            ))}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div style={{ fontSize: 11, color: T.danger, textAlign: 'center', fontFamily: T.font }}>
            {error}
          </div>
        )}

        {/* Next Button */}
        <button
          disabled={checking}
          onClick={handleNext}
          style={{
            ...actionBtn('Next', T.teal, T.white),
            width: '100%',
            padding: 13,
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {checking ? 'Checking…' : 'Next'}
        </button>
      </div>
    </div>
    </WebEntryLayout>
  );
}
